import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Box,
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';

import { MarketingPageShell } from '@/components/Marketing/MarketingPageShell';
import { usePageMeta } from '@/hooks/usePageMeta';

const RESUME_EMAIL = 'support@unbeatableloans.com';
const RESUME_SUBJECT = 'Careers – Mortgage Loan Officer';

export default function CareersPage() {
  usePageMeta(
    'Careers',
    'Mortgage loan officer and partnership opportunities at Unbeatable Loans—remote-friendly, mission-driven purchase experience without the traditional realtor middle.'
  );

  return (
    <MarketingPageShell
      title="Careers at Unbeatable Loans"
      subtitle="Mortgage loan officer roles and partnership paths for licensed professionals who want to help buyers and investors win—with technology, wholesale rates, and a better purchase experience."
    >
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3, lineHeight: 1.85, maxWidth: 900 }}
      >
        Unbeatable Loans exists to help people buy and finance real estate with
        clarity: loan estimates tied to real listings, offer workflows that do
        not depend on a buyer&apos;s agent commission grab, and broker-led
        guidance from someone who understands both the mortgage and the deal. If
        that mission resonates and you bring the right licenses and experience,
        we want to talk about how you can grow with us—often remotely, always
        with a team that cares about outcomes, not middleman theater.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Open roles: mortgage loan officer (MLO)
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, lineHeight: 1.8 }}
      >
        We hire and partner with mortgage loan officers who can originate in our
        model: wholesale execution, investor and primary-residence programs, and
        clients who are often buying with less friction on the agent side. You
        are the expert who helps them choose the right structure, lock
        confidently, and close on time.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Compensation
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.8 }}
        >
          Compensation is{' '}
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            determined between you and the brokerage
          </Box>
          : it may be structured as a salaried arrangement, commission-based, or
          a hybrid, depending on role scope, volume, and how we align incentives
          for long-term partnership. We discuss specifics only with qualified
          candidates after an introductory conversation—no cookie-cutter grid
          that ignores how you actually work.
        </Typography>
      </Paper>

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        What we look for
      </Typography>
      <List dense sx={{ mb: 3 }}>
        {[
          'Active mortgage loan originator license (state-appropriate) in good standing—we will verify NMLS and any required state registrations.',
          'Prior experience as a mortgage broker or MLO in a broker channel: you know how to shop wholesale, read AUS findings, and counsel borrowers under pressure.',
          'Experience as a real estate agent or equivalent transaction-side exposure: you understand contracts, contingencies, timelines, and how agents and lenders intersect—because our clients often skip the buyer agent and need a trusted finance partner who speaks that language.',
          'Comfort with technology: our platform, Chrome tooling, and fast-moving rate environment are core to how we deliver “unbeatable” outcomes.',
          'Clear written and verbal communication and disciplined documentation—remote teams depend on it.',
        ].map((text) => (
          <ListItem
            key={text}
            sx={{ alignItems: 'flex-start', py: 0.75, pl: 0 }}
          >
            <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
              <CheckCircleOutlineIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={text}
              primaryTypographyProps={{
                variant: 'body2',
                color: 'text.secondary',
                lineHeight: 1.75,
              }}
            />
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Partnership, not just a desk
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.85 }}
          >
            We are built for professionals who want{' '}
            <Box
              component="span"
              sx={{ fontWeight: 600, color: 'text.primary' }}
            >
              partnership
            </Box>
            : shared brand, shared systems, and alignment on how borrowers
            should be treated. That can mean originating under our umbrella with
            defined splits and support, contributing to investor and purchase
            workflows, or evolving toward deeper collaboration as we scale. The
            right fit is someone who wants to build a book and a reputation
            alongside a brokerage that is actively investing in tech and
            education—not someone looking for a static call-center chair.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.85 }}
          >
            Partnership also means transparency on compliance, marketing, and
            how we represent fees and deliverables to consumers. You should
            expect clear agreements, mentorship where helpful, and room to grow
            whether you focus on primary homes, DSCR, fix-and-flip, or a mix—as
            long as we stay aligned on Reg N, state rules, and the standards
            that protect both clients and licensees.
          </Typography>
        </Grid>
      </Grid>

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Remote-first work
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, lineHeight: 1.85 }}
      >
        Most day-to-day work—borrower calls, lender follow-ups, document
        chasing, and internal collaboration—happens over video, phone, and our
        internal tools. We care about output and client experience, not which
        desk you occupy. You still need a professional environment for
        confidential conversations, secure handling of non-public information,
        and licensing compliance in the states where you originate.
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 4, lineHeight: 1.85 }}
      >
        Benefits of remote partnership with Unbeatable include flexible
        scheduling around high-intensity weeks (locks, underwriting turnarounds,
        closing timelines), less commute friction, and the ability to serve
        borrowers across the markets we are licensed to operate in—paired with a
        culture that defaults to documentation and async updates so nothing
        falls through the cracks.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Why “without a realtor” matters—for you and the client
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, lineHeight: 1.85 }}
      >
        Many of our clients are intentionally buying without a traditional
        buyer&apos;s agent. That does not mean they are adrift: it means they
        want to allocate dollars to closing, rate buydown, or reserves instead
        of a commission split—and they need a mortgage partner who can explain
        tradeoffs, keep contingencies sensible, and stay nimble when the listing
        agent or title shop moves fast.
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 4, lineHeight: 1.85 }}
      >
        For an MLO with broker and agent experience, this is one of the most
        rewarding parts of the job. You are not just “taking an app”; you are
        helping someone feel confident in the largest financial decision of
        their life while the industry still assumes an agent is in the middle.
        When it goes well, clients save meaningful money and you become the
        person they trust for the next deal and referrals—not because you sold
        them a gimmick, but because you executed.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: 'action.hover',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          How to apply
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.8 }}
        >
          Email your resume and a short note on your licenses, production
          history, and why you want to work in a purchase-focused, tech-forward
          brokerage. We reply to serious inquiries; please no solicitations from
          unlicensed recruiters.
        </Typography>
        <Button
          variant="contained"
          size="large"
          href={`mailto:${RESUME_EMAIL}?subject=${encodeURIComponent(
            RESUME_SUBJECT
          )}`}
          sx={{ fontWeight: 700, borderRadius: 2, mr: 2, mb: { xs: 1, sm: 0 } }}
        >
          Email your resume
        </Button>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 1 }}
        >
          {RESUME_EMAIL} · use subject line: {RESUME_SUBJECT}
        </Typography>
      </Paper>
    </MarketingPageShell>
  );
}
